import { LayoutGrid, List, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getProducts, getProductsByCategory } from "../api/products";
import { Pagination } from "../components/Pagination";
import { ProductCard } from "../components/ProductCard";
import { ProductListRow } from "../components/ProductListRow";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useDebounce } from "../hooks/useDebounce";
import { safeGetItem, safeSetItem, startFreshGuestSession } from "../utils/safeStorage";

const DEFAULT_PAGE_SIZE = 20;
const PRODUCT_VIEW_STORAGE_KEY = "platterProductView";
const TABLET_MEDIA_QUERY = "(min-width: 640px)";

function getInitialViewMode() {
  const savedView = safeGetItem(PRODUCT_VIEW_STORAGE_KEY);
  return savedView === "list" || savedView === "grid" ? savedView : "grid";
}

export function Products({ selectedCategory }) {
  const [searchParams] = useSearchParams();
  const { loadCart, refreshGuestId, resetCart } = useCart();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [canUseViewToggle, setCanUseViewToggle] = useState(false);
  const [meta, setMeta] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const productsTopRef = useRef(null);
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    const queryValue = searchParams.get("search") || "";
    setSearch(queryValue);
  }, [searchParams]);

  useEffect(() => {
    safeSetItem(PRODUCT_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(TABLET_MEDIA_QUERY);
    const handleMediaChange = () => setCanUseViewToggle(mediaQuery.matches);

    handleMediaChange();
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  useEffect(() => {
    setPage(1);
    productsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [debouncedSearch, selectedCategory?.CategoryID]);

  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;

    async function loadProducts() {
      try {
        setLoading(true);
        setError("");

        const requestProducts = () =>
          selectedCategory?.CategoryID
            ? getProductsByCategory(selectedCategory.CategoryID, {
                page,
                pageSize: DEFAULT_PAGE_SIZE,
                search: debouncedSearch,
                signal: controller.signal,
              })
            : getProducts(page, DEFAULT_PAGE_SIZE, debouncedSearch, controller.signal);

        let response;
        try {
          response = await requestProducts();
        } catch (firstError) {
          if (firstError.name === "CanceledError" || firstError.code === "ERR_CANCELED") {
            throw firstError;
          }

          console.warn("Product API failed. Retrying once without changing GuestId.", {
            status: firstError?.response?.status,
            message: firstError?.message,
          });
          response = await requestProducts();
        }

        if (!ignore) {
          setProducts(response.items);
          setMeta({
            page: response.page,
            totalItems: response.totalItems,
            totalPages: response.totalPages,
          });
        }
      } catch (requestError) {
        if (ignore || requestError.name === "CanceledError" || requestError.code === "ERR_CANCELED") {
          return;
        }

        setError("Products could not be loaded.");
        setProducts([]);
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [debouncedSearch, page, reloadKey, selectedCategory]);

  async function handleRetryProducts() {
    setReloadKey((current) => current + 1);
  }

  async function handleStartFreshSession() {
    const confirmed = window.confirm(
      "Starting a fresh session may clear your current guest cart. Continue?"
    );
    if (!confirmed) {
      return;
    }

    const nextGuestId = startFreshGuestSession();
    console.log("Start fresh session selected. New GuestId:", nextGuestId);
    resetCart();
    refreshGuestId();
    await loadCart();
    setPage(1);
    setReloadKey((current) => current + 1);
  }

  const effectiveViewMode = canUseViewToggle ? viewMode : "grid";

  return (
    <div className="space-y-5 pb-24 sm:pb-20" ref={productsTopRef}>
      <section className="min-w-0 space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Pickup catalogue
            </p>
            <h1 className="mt-2 font-display text-3xl font-semibold text-primary sm:text-4xl">
              {selectedCategory?.CategoryName || "Products"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              Browse available items, choose quantities, and add them to the guest cart.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-muted-foreground">
            {meta.totalItems} {meta.totalItems === 1 ? "product" : "products"}
            {debouncedSearch ? ` for "${debouncedSearch}"` : ""}
          </p>
          <div className="hidden rounded-xl border border-border bg-background p-1 sm:inline-flex">
            <button
              className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${
                viewMode === "grid" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-accent"
              }`}
              onClick={() => setViewMode("grid")}
              title="Grid View"
              type="button"
            >
              <LayoutGrid className="h-4 w-4" />
              <span>Grid</span>
            </button>
            <button
              className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-semibold transition ${
                viewMode === "list" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-accent"
              }`}
              onClick={() => setViewMode("list")}
              title="List View"
              type="button"
            >
              <List className="h-4 w-4" />
              <span>List</span>
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            <p className="font-semibold">We had trouble loading the menu.</p>
            <p className="mt-1">{error}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={handleRetryProducts} size="sm" type="button">
                Retry
              </Button>
              <Button onClick={handleStartFreshSession} size="sm" type="button" variant="secondary">
                Start fresh session
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <Card className="flex min-h-56 items-center justify-center border-border bg-card shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </Card>
        ) : products.length ? (
          <>
            {effectiveViewMode === "grid" ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 min-[1100px]:grid-cols-4 min-[1500px]:grid-cols-5">
                {products.map((product) => (
                  <ProductCard key={product.productId} product={product} />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {products.map((product) => (
                  <ProductListRow key={product.productId} product={product} />
                ))}
              </div>
            )}
            <Pagination
              onNext={() => setPage((currentPage) => currentPage + 1)}
              onPrevious={() => setPage((currentPage) => currentPage - 1)}
              page={meta.page}
              totalPages={meta.totalPages}
            />
          </>
        ) : (
          <Card className="border-border bg-card px-6 py-12 text-center shadow-sm">
            <h2 className="text-xl font-semibold">No products found</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Try a different search term or product code.
            </p>
          </Card>
        )}
      </section>
    </div>
  );
}
